import React, { FC, useState } from 'react';
import { Box, Divider, Grid } from '@mui/material';

import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Dropdown from '@/components/Dropdown/Dropdown';
import Input from '@/components/Input/Input';

import styles from './FZStoreModifierFilter.module.css';
import { useTranslation } from 'react-i18next';
import MaskedInput from '@/components/MaskedInput/MaskedInput';
import { FilteredModifierValues } from '@/types/menus.types';

const DEFAULT_CATEGORY_NAME = 'Pizza Cheese';

type FZStoreMenuFilterProps = {
  categories: {
    value: string;
    label: string;
  }[];
  subCategories: {
    value: string;
    label: string;
  }[];
  isLoadingCategories: boolean;
  categoryName: string;
  onApplyFilter: (filteredValues: FilteredModifierValues) => void;
  onCategoryChange: (newCategory: string) => void;
  onClearFilter: () => void;
  searching: boolean;
};

const FZStoreModifierFilter: FC<FZStoreMenuFilterProps> = ({
  categories,
  categoryName,
  onCategoryChange,
  subCategories,
  isLoadingCategories,
  onApplyFilter,
  onClearFilter,
  searching,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [option1, setOption1] = useState('');
  const [option2, setOption2] = useState('');
  const [category, setCategory] = useState(DEFAULT_CATEGORY_NAME);
  const [subCategoryName, setSubCategoryName] = useState('');

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setPrice(value);
  };
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setName(value);
  };

  const handleOption1 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setOption1(value);
  };
  const handleOption2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setOption2(value);
  };
  const handleCategory = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    setName('');
    setPrice('');
    setOption1('');
    setOption2('');
    setSubCategoryName('');
    onCategoryChange(value);
  };

  return (
    <Box
      boxShadow="grey"
      mb={4}
      borderColor="red"
      borderRadius="10px"
      sx={{
        boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
        width: { xs: '96%', sm: '96%', md: '97%', lg: '97%' },
      }}
    >
      <Grid container direction="row" p={3} style={{ maxWidth: '100vw' }}>
        <Grid item paddingRight={3}>
          <Grid container direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'row' }} spacing={2}>
            <Grid item minWidth="165px">
              <Dropdown
                label={t('common.category')}
                id="category"
                options={categories}
                loading={isLoadingCategories}
                hidePlaceholder={false}
                value={categoryName}
                onChange={(e) => handleCategory(e)}
                className={styles.dropdownSize}
              />
            </Grid>
            {subCategories.length > 0 && (
              <Grid item minWidth="165px">
                <Dropdown
                  label={t('common.subcategory')}
                  id="subcategory"
                  options={subCategories}
                  loading={isLoadingCategories}
                  hidePlaceholder={false}
                  onChange={(e) => setSubCategoryName(e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Divider orientation="vertical" variant="middle" flexItem />
        <Grid item paddingLeft={3} xs={5} sm={7} md={8} lg={7} xl={8}>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={6} sm={6} md={3}>
              <Input
                label={t('common.modifier')}
                value={name}
                id="modifier"
                onChange={(e) => handleDescriptionChange(e)}
                subText={t('common.helpTextModifierHint')}
                subTextClassName={styles.subtextOverride}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <MaskedInput
                id="price"
                label={t('common.price')}
                prefix="$"
                fixedDecimalScale
                decimalScale={2}
                value={price}
                onChange={handlePriceChange}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Input
                label={t('common.option1')}
                value={option1}
                id="option1"
                subText={t('common.helpTextOption1Hint')}
                onChange={(e) => handleOption1(e)}
                subTextClassName={styles.subtextOverride}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Input label={t('common.option2')} value={option2} id="option2" onChange={(e) => handleOption2(e)} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Box p={2} sx={{ backgroundColor: 'var(--black-100)' }}>
        <Grid container direction="row">
          <Grid item>
            <Button
              variant={ButtonVariants.Secondary}
              isLoading={searching}
              onClick={() =>
                onApplyFilter({
                  modifierName: name,
                  subCategory: subCategoryName,
                  price,
                  option1,
                  option2,
                })
              }
            >
              {t('common.searchModifiers')}
            </Button>
          </Grid>
          <Grid item marginTop={1} ml={2}>
            <Button
              variant={ButtonVariants.Text}
              onClick={(e) => {
                setCategory(DEFAULT_CATEGORY_NAME);
                onCategoryChange(DEFAULT_CATEGORY_NAME);
                setName('');
                setPrice('');
                setOption1('');
                setOption2('');
                onClearFilter();
              }}
            >
              {t('common.clearFiltersButton')}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FZStoreModifierFilter;
